import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { leadService } from '../services/api';
import { Spinner } from '../components/Spinner';
import { DrillDownPanel } from '../components/reports/DrillDownPanel';
import { useDrillDown } from '../hooks/useDrillDown';
import { ChartPieIcon } from '@heroicons/react/24/outline';
import { RangePicker, RangeNote, BLANK_RANGE_FILTERS, drillDates } from '../components/reports/ReportRange';
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend,
} from 'recharts';

const REASON_COLORS = { lost_to_codealers: '#f59e0b', lost_to_competitions: '#ef4444', not_purchased: '#8b5cf6', other: '#94a3b8' };
const FOLLOWUP_COLORS = { first: '#60a5fa', second: '#34d399', third: '#fbbf24', fourPlus: '#f87171' };
const FOLLOWUP_LABELS = { first: '1st Follow-up', second: '2nd Follow-up', third: '3rd Follow-up', fourPlus: '4+ Follow-ups' };

// A count cell/card that toggles an inline drill-down when clicked.
const DrillTrigger = ({ count, active, onToggle, className = '', as: Tag = 'button' }) => {
  if (!count) return <span className={className}>{count || 0}</span>;
  return (
    <Tag onClick={onToggle} className={`hover:underline ${active ? 'underline' : ''} ${className}`}>
      {count}
    </Tag>
  );
};

export const LostReport = () => {
  const [filters, setFilters] = useState(BLANK_RANGE_FILTERS);
  const [applied, setApplied] = useState(BLANK_RANGE_FILTERS);

  const { data, isLoading } = useQuery(
    ['lostReport', applied],
    () => leadService.getLostReport(applied),
    { keepPreviousData: true }
  );

  // The window the API actually used — drives the note and the drill-downs.
  const appliedRange = data?.data?.data?.range;

  const drill = useDrillDown(leadService.getLeads);
  const { expanded, close, search, setSearch, leads, total, loading: drillLoading, loadingMore, hasMore, loadMore } = drill;

  const toggle = (key, title, params) => {
    drill.toggle(key, title, { ...drillDates(appliedRange), ...params });
  };

  const d = data?.data?.data;
  const funnel = d?.funnel;
  const byReason = d?.byReason || [];
  const byEmployee = d?.byEmployee || [];
  const byModel = d?.byModel || [];
  const followupSummary = d?.followupSummary;
  const byFollowupEmployee = d?.byFollowupEmployee || [];
  const byFollowupModel = d?.byFollowupModel || [];

  const pieData = byReason.filter(r => r.count > 0).map(r => ({ name: r.label, value: r.count, key: r.key }));
  const followupPieData = followupSummary
    ? Object.keys(FOLLOWUP_LABELS).map(key => ({ name: FOLLOWUP_LABELS[key], value: followupSummary[key], key })).filter(r => r.value > 0)
    : [];

  const panelProps = expanded ? {
    title: expanded.title,
    leads,
    total,
    isLoading: drillLoading,
    onClose: close,
    search, onSearchChange: setSearch,
    hasMore, onLoadMore: loadMore, loadingMore,
  } : null;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <ChartPieIcon className="h-6 w-6 text-primary-600" />
          Lost Report
        </h1>
        <p className="mt-1 text-gray-600">Lost-lead analysis — funnel summary, reasons, employee-wise and model-wise breakdown.</p>
      </div>

      {/* Filters */}
      <div className="bg-white shadow-sm rounded-xl border border-gray-100 mb-4 px-5 py-4 flex flex-wrap items-center gap-2">
        <RangePicker value={filters} onChange={setFilters} />
        <button onClick={() => { setApplied(filters); close(); }} className="btn-primary text-sm">Search</button>
        <button onClick={() => { setFilters(BLANK_RANGE_FILTERS); setApplied(BLANK_RANGE_FILTERS); close(); }} className="btn-outline-secondary text-sm">Clear Filter</button>
        <div className="w-full"><RangeNote applied={appliedRange} /></div>
      </div>

      {isLoading ? (
        <div className="p-12 text-center"><Spinner size="lg" /></div>
      ) : !funnel ? (
        <div className="bg-white rounded-xl border border-gray-100 p-12 text-center text-gray-400 text-sm">No data for this period.</div>
      ) : (
        <>
          {/* Funnel summary cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-1">
            <div className="bg-white rounded-xl border border-gray-100 p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Total</p>
              <DrillTrigger
                count={funnel.total}
                active={expanded?.key === 'total'}
                onToggle={() => toggle('total', 'Total Leads', {})}
                className="block text-2xl font-bold text-gray-900 mt-1 text-primary-600"
              />
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide">WIP</p>
              <p className="text-2xl font-bold text-gray-700 mt-1">{funnel.wip}</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Converted</p>
              <DrillTrigger
                count={funnel.converted}
                active={expanded?.key === 'converted'}
                onToggle={() => toggle('converted', 'Converted Leads', { status: 'converted' })}
                className="block text-2xl font-bold mt-1 text-emerald-700"
              />
            </div>
            <div className="bg-white rounded-xl border border-gray-100 p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Lost</p>
              <DrillTrigger
                count={funnel.lost}
                active={expanded?.key === 'lost'}
                onToggle={() => toggle('lost', 'Lost Leads', { status: 'lost' })}
                className="block text-2xl font-bold mt-1 text-red-600"
              />
            </div>
          </div>
          {panelProps && ['total', 'converted', 'lost'].includes(expanded.key) && (
            <div className="mb-4"><DrillDownPanel asRow={false} {...panelProps} /></div>
          )}

          {/* Donut chart — lost by reason */}
          {pieData.length > 0 && (
            <div className="bg-white shadow-sm rounded-xl border border-gray-100 p-5 mb-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Lost leads by reason</h3>
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={2}>
                    {pieData.map((entry, i) => <Cell key={i} fill={REASON_COLORS[entry.key] || '#94a3b8'} />)}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Donut chart — lost by follow-up count */}
          {followupPieData.length > 0 && (
            <div className="bg-white shadow-sm rounded-xl border border-gray-100 p-5 mb-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Lost leads by no. of follow-ups</h3>
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie data={followupPieData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={2}>
                    {followupPieData.map((entry, i) => <Cell key={i} fill={FOLLOWUP_COLORS[entry.key] || '#94a3b8'} />)}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Employee-wise table */}
          <div className="bg-white shadow-sm rounded-xl border border-gray-100 overflow-hidden mb-4">
            <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-800">Employee-wise Lost Leads</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-900">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide">Sales Executive</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide">Lost to Co-dealer</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide">Lost to Competitor</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide">Not Purchased</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide">Lost Leads</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {byEmployee.length === 0 ? (
                    <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-400">No lost leads.</td></tr>
                  ) : byEmployee.map(e => {
                    const uid = e._id;
                    const rowKey = `emp-${uid || 'unassigned'}`;
                    const base = { status: 'lost', assignedTo: uid };
                    const userName = e.user ? `${e.user.firstName} ${e.user.lastName}` : 'Unassigned';
                    return (
                      <React.Fragment key={rowKey}>
                        <tr className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium text-gray-900">{userName}</td>
                          <td className="px-4 py-3">
                            <DrillTrigger count={e.lost_to_codealers} active={expanded?.key === `${rowKey}-c`} className="text-primary-600 font-medium"
                              onToggle={() => uid && toggle(`${rowKey}-c`, `${userName} — Lost to Co-dealer`, { ...base, activitySubTypeKey: 'lost_to_codealers' })} />
                          </td>
                          <td className="px-4 py-3">
                            <DrillTrigger count={e.lost_to_competitions} active={expanded?.key === `${rowKey}-k`} className="text-primary-600 font-medium"
                              onToggle={() => uid && toggle(`${rowKey}-k`, `${userName} — Lost to Competitor`, { ...base, activitySubTypeKey: 'lost_to_competitions' })} />
                          </td>
                          <td className="px-4 py-3">
                            <DrillTrigger count={e.not_purchased} active={expanded?.key === `${rowKey}-n`} className="text-primary-600 font-medium"
                              onToggle={() => uid && toggle(`${rowKey}-n`, `${userName} — Not Purchased`, { ...base, activitySubTypeKey: 'not_purchased' })} />
                          </td>
                          <td className="px-4 py-3">
                            <DrillTrigger count={e.total} active={expanded?.key === `${rowKey}-t`} className="text-primary-600 font-semibold"
                              onToggle={() => uid && toggle(`${rowKey}-t`, `${userName} — All Lost`, base)} />
                          </td>
                        </tr>
                        {expanded?.key?.startsWith(rowKey) && panelProps && (
                          <DrillDownPanel colSpan={5} {...panelProps} />
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Model-wise table */}
          <div className="bg-white shadow-sm rounded-xl border border-gray-100 overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-800">Model-wise Lost Leads</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-900">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide">Brand / Model</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide">Lost to Co-dealer</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide">Lost to Competitor</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide">Not Purchased</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide">Lost Leads</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {byModel.length === 0 ? (
                    <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-400">No lost leads.</td></tr>
                  ) : byModel.map((m, i) => {
                    const rowKey = `mdl-${i}`;
                    const base = { status: 'lost', brand: m.brand, model: m.model };
                    const label = [m.brand, m.model].filter(Boolean).join(' ') || 'Unspecified';
                    return (
                      <React.Fragment key={rowKey}>
                        <tr className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium text-gray-900">{label}</td>
                          <td className="px-4 py-3">
                            <DrillTrigger count={m.lost_to_codealers} active={expanded?.key === `${rowKey}-c`} className="text-primary-600 font-medium"
                              onToggle={() => toggle(`${rowKey}-c`, `${label} — Lost to Co-dealer`, { ...base, activitySubTypeKey: 'lost_to_codealers' })} />
                          </td>
                          <td className="px-4 py-3">
                            <DrillTrigger count={m.lost_to_competitions} active={expanded?.key === `${rowKey}-k`} className="text-primary-600 font-medium"
                              onToggle={() => toggle(`${rowKey}-k`, `${label} — Lost to Competitor`, { ...base, activitySubTypeKey: 'lost_to_competitions' })} />
                          </td>
                          <td className="px-4 py-3">
                            <DrillTrigger count={m.not_purchased} active={expanded?.key === `${rowKey}-n`} className="text-primary-600 font-medium"
                              onToggle={() => toggle(`${rowKey}-n`, `${label} — Not Purchased`, { ...base, activitySubTypeKey: 'not_purchased' })} />
                          </td>
                          <td className="px-4 py-3">
                            <DrillTrigger count={m.total} active={expanded?.key === `${rowKey}-t`} className="text-primary-600 font-semibold"
                              onToggle={() => toggle(`${rowKey}-t`, `${label} — All Lost`, base)} />
                          </td>
                        </tr>
                        {expanded?.key?.startsWith(rowKey) && panelProps && (
                          <DrillDownPanel colSpan={5} {...panelProps} />
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Employee-wise (Assigned) Lost Leads by No. of Follow-ups */}
          <div className="bg-white shadow-sm rounded-xl border border-gray-100 overflow-hidden mb-4 mt-4">
            <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-800">Employee-wise Lost Leads by No. of Follow-ups</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-900">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide">Sales Executive</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide">1st Follow-up</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide">2nd Follow-up</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide">3rd Follow-up</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide">4+ Follow-ups</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {byFollowupEmployee.length === 0 ? (
                    <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-400">No lost leads.</td></tr>
                  ) : byFollowupEmployee.map(e => {
                    const uid = e._id;
                    const rowKey = `fu-emp-${uid || 'unassigned'}`;
                    const userName = e.user ? `${e.user.firstName} ${e.user.lastName}` : 'Unassigned';
                    return (
                      <React.Fragment key={rowKey}>
                        <tr className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium text-gray-900">{userName}</td>
                          <td className="px-4 py-3 text-gray-700">{e.first}</td>
                          <td className="px-4 py-3 text-gray-700">{e.second}</td>
                          <td className="px-4 py-3 text-gray-700">{e.third}</td>
                          <td className="px-4 py-3 text-gray-700">{e.fourPlus}</td>
                          <td className="px-4 py-3">
                            <DrillTrigger count={e.total} active={expanded?.key === rowKey} className="text-primary-600 font-semibold"
                              onToggle={() => uid && toggle(rowKey, `${userName} — All Lost`, { status: 'lost', assignedTo: uid })} />
                          </td>
                        </tr>
                        {expanded?.key === rowKey && panelProps && (
                          <DrillDownPanel colSpan={6} {...panelProps} />
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Model-wise Lost Leads by No. of Follow-ups */}
          <div className="bg-white shadow-sm rounded-xl border border-gray-100 overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-800">Model-wise Lost Leads by No. of Follow-ups</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-900">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide">Brand / Model</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide">1st Follow-up</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide">2nd Follow-up</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide">3rd Follow-up</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide">4+ Follow-ups</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {byFollowupModel.length === 0 ? (
                    <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-400">No lost leads.</td></tr>
                  ) : byFollowupModel.map((m, i) => {
                    const rowKey = `fu-mdl-${i}`;
                    const label = [m.brand, m.model].filter(Boolean).join(' ') || 'Unspecified';
                    return (
                      <React.Fragment key={rowKey}>
                        <tr className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium text-gray-900">{label}</td>
                          <td className="px-4 py-3 text-gray-700">{m.first}</td>
                          <td className="px-4 py-3 text-gray-700">{m.second}</td>
                          <td className="px-4 py-3 text-gray-700">{m.third}</td>
                          <td className="px-4 py-3 text-gray-700">{m.fourPlus}</td>
                          <td className="px-4 py-3">
                            <DrillTrigger count={m.total} active={expanded?.key === rowKey} className="text-primary-600 font-semibold"
                              onToggle={() => toggle(rowKey, `${label} — All Lost`, { status: 'lost', brand: m.brand, model: m.model })} />
                          </td>
                        </tr>
                        {expanded?.key === rowKey && panelProps && (
                          <DrillDownPanel colSpan={6} {...panelProps} />
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
